import {
  ApolloLink,
  FetchResult,
  Observable,
  Operation,
  split,
} from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { ClientOptions, createClient } from 'graphql-sse';

function stringHeaders(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const headers: Record<string, string> = {};
  for (const [name, headerValue] of Object.entries(value)) {
    if (typeof headerValue === 'string') {
      headers[name] = headerValue;
    }
  }
  return headers;
}

export function getOktaAuthorizationHeaders(): Record<string, string> {
  const storedTokens =
    sessionStorage.getItem('okta-token-storage') ||
    localStorage.getItem('okta-token-storage');
  if (!storedTokens) {
    return {};
  }
  const accessToken = (
    JSON.parse(storedTokens) as {
      accessToken?: { value?: string };
    }
  ).accessToken?.value;
  return accessToken
    ? { Authorization: ['Bearer', accessToken].join(' ') }
    : {};
}

export class SSELink extends ApolloLink {
  constructor(private readonly options: ClientOptions<false>) {
    super();
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable((sink) => {
      const configuredHeaders = this.options.headers;
      const client = createClient({
        ...this.options,
        headers: async (request) => ({
          ...stringHeaders(operation.getContext()['headers']),
          ...stringHeaders(
            typeof configuredHeaders === 'function'
              ? await configuredHeaders(request)
              : configuredHeaders
          ),
        }),
      });

      return client.subscribe<FetchResult>(
        {
          query: print(operation.query),
          variables: operation.variables,
          operationName: operation.operationName,
          extensions: operation.extensions,
        },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        }
      );
    });
  }
}

/**
 * Reads the Okta access token from storage and, when present, attaches it as
 * a `Bearer` Authorization header on the request context. `extraHeaders` lets
 * a given app merge additional static headers (e.g. a request timeout) onto
 * the same link without duplicating the token-reading logic.
 */
export function createOktaAuthLink(
  extraHeaders?: Record<string, string>
): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const accessToken = JSON.parse(
      sessionStorage.getItem('okta-token-storage') ||
        localStorage.getItem('okta-token-storage')
    )?.accessToken;

    if (accessToken && accessToken.value) {
      operation.setContext({
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          ...extraHeaders,
        },
      });
    }
    return forward(operation);
  });
}

/**
 * Operation names that the subscription service also exposes over its plain
 * HTTP endpoint (as opposed to the SSE transport). These must reach the same
 * `/graphql` route as `subscriptionLink` handles for actual subscriptions.
 */
export const SUBSCRIPTION_SERVICE_HTTP_OPERATIONS = [
  'echo',
  'registerPolling',
  'unregisterKey',
  'poll',
] as const;

/**
 * Routes GraphQL operations to the subscription service (over SSE for
 * `subscription` operations, over its dedicated HTTP link for the named
 * operations it also serves) and everything else to the app's default HTTP
 * link. This is the split/link wiring every app previously duplicated in its
 * own `graphql.module.ts`.
 */
export function createSubscriptionRoutingLink(
  subscriptionLink: ApolloLink,
  subscriptionHttpLink: ApolloLink,
  defaultHttpLink: ApolloLink,
  httpOperationNames: readonly string[] = SUBSCRIPTION_SERVICE_HTTP_OPERATIONS
): ApolloLink {
  return split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    subscriptionLink,
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return httpOperationNames.includes(definition?.name?.value ?? '');
      },
      subscriptionHttpLink,
      defaultHttpLink
    )
  );
}
